import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  Chip,
  IconButton,
  Tooltip,
  Avatar,
  Skeleton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  MenuItem,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import Icon from 'src/@core/components/icon';
import { GuardWrapper } from 'src/layouts/GuardWrapper';
import axios from 'axios';

// ─── Constants ────────────────────────────────────────────────────────────────
const userData = JSON.parse(localStorage.getItem('userData') || '{}');
const subdomain = userData?.domain?.name || 'health';
const BRAND = '#750f1f';
const BRAND_DARK = '#5e0c19';
const ROWS_PER_PAGE_OPTIONS = [10, 25, 50];

const ROLE_CHIP = {
  superadmin: { label: 'Super Admin', color: BRAND, bg: '#fdecea' },
  admin: { label: 'Admin', color: '#1565c0', bg: '#e3f2fd' },
  user: { label: 'User', color: '#2e7d32', bg: '#e8f5e9' },
};

const STATUS_CHIP = {
  active: { label: 'Active', color: '#2e7d32', bg: '#e8f5e9' },
  inactive: { label: 'Inactive', color: '#757575', bg: '#f5f5f5' },
  suspended: { label: 'Suspended', color: '#e65100', bg: '#fff3e0' },
};

function getInitials(name = '') {
  return name
    .split(' ')
    .map((n) => n[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

function stringToColor(str = '') {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const hue = Math.abs(hash) % 360;
  return `hsl(${hue}, 45%, 45%)`;
}

// ─── Skeleton rows ─────────────────────────────────────────────────────────────
function SkeletonRows({ count = 8 }) {
  return Array.from({ length: count }).map((_, i) => (
    <TableRow key={i}>
      {[48, 140, 180, 90, 90, 100, 90].map((w, j) => (
        <TableCell key={j}>
          <Skeleton variant={j === 0 ? 'circular' : 'text'} width={j === 0 ? 36 : w} height={j === 0 ? 36 : 20} />
        </TableCell>
      ))}
    </TableRow>
  ));
}

// ─── Main component ────────────────────────────────────────────────────────────
const AllUsersPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const [users, setUsers] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, user: null });
  const [deleting, setDeleting] = useState(false);

  // ── Guard ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    const stored = JSON.parse(localStorage.getItem('userData') || '{}');
    if (stored.isSuperAdmin !== true) {
      toast.error('Only Super Admins can access this page');
      navigate('/dashboard');
    }
  }, [navigate]);

  // ── Fetch users ────────────────────────────────────────────────────────────
  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('accessToken');
      const { data } = await axios.get('/api/jwt/all-users', {
        headers: {
          Authorization: `Bearer ${token}`,
          'x-subdomain': subdomain,
        },
      });
      // Support { users: [...] } or plain array
      const list = Array.isArray(data) ? data : data.users ?? [];
      setUsers(list);
      setFiltered(list);
    } catch (err) {
      console.error(err.response?.data || err.message);
      toast.error(err.response?.data?.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // ── Filter logic ───────────────────────────────────────────────────────────
  useEffect(() => {
    const q = search.toLowerCase();
    setFiltered(
      users.filter((u) => {
        const matchSearch =
          !q ||
          u.fullname?.toLowerCase().includes(q) ||
          u.username?.toLowerCase().includes(q) ||
          u.email?.toLowerCase().includes(q) ||
          u.rank?.toLowerCase().includes(q) ||
          u.country?.toLowerCase().includes(q);
        const matchRole = roleFilter === 'all' || u.role === roleFilter;
        return matchSearch && matchRole;
      })
    );
    setPage(0);
  }, [search, roleFilter, users]);

  // ── Delete ─────────────────────────────────────────────────────────────────
  const handleDeleteConfirm = async () => {
    // const { user } = deleteDialog;
    // setDeleting(true);
    // try {
    //   const token = localStorage.getItem('accessToken');
    //   await axios.delete(`/api/jwt/users/${user._id}`, {
    //     headers: {
    //       Authorization: `Bearer ${token}`,
    //       'x-subdomain': subdomain,
    //     },
    //   });
    //   toast.success(`${user.fullname} removed`);
    //   setDeleteDialog({ open: false, user: null });
    //   fetchUsers();
    // } catch (err) {
    //   toast.error(err.response?.data?.message || 'Delete failed');
    // } finally {
    //   setDeleting(false);
    // }
  };

  // ── Paginated slice ────────────────────────────────────────────────────────
  const paginated = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <GuardWrapper>
      <Box sx={{ p: { xs: 3, md: 6 } }}>

        {/* ── Header ── */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 5, flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h5" fontWeight={700} color="text.primary">
              All Users
            </Typography>
            <Typography variant="body2" color="text.secondary" mt={0.5}>
              {loading ? '—' : `${filtered.length} of ${users.length} users`}
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<Icon icon="bx:user-plus" />}
            onClick={() => navigate('/register-user')}
            sx={{
              backgroundColor: BRAND,
              '&:hover': { backgroundColor: BRAND_DARK },
              borderRadius: 2,
              px: 3,
            }}
          >
            Add User
          </Button>
        </Box>

        {/* ── Filters ── */}
        <Box sx={{ display: 'flex', gap: 2, mb: 4, flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder="Search by name, email, rank…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Icon icon="bx:search" fontSize={18} />
                </InputAdornment>
              ),
              endAdornment: search ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearch('')}>
                    <Icon icon="bx:x" fontSize={16} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
            sx={{ minWidth: 280, backgroundColor: theme.palette.background.paper, borderRadius: 2 }}
          />
          <TextField
            select
            size="small"
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            sx={{ minWidth: 140, backgroundColor: theme.palette.background.paper, borderRadius: 2 }}
          >
            <MenuItem value="all">All Roles</MenuItem>
            <MenuItem value="user">User</MenuItem>
            <MenuItem value="admin">Admin</MenuItem>
            <MenuItem value="superadmin">Super Admin</MenuItem>
          </TextField>
        </Box>

        {/* ── Table ── */}
        <Card sx={{ borderRadius: 3, boxShadow: theme.shadows[2] }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: theme.palette.action.hover }}>
                  {['User', 'Email', 'Rank / Title', 'Country', 'Role', 'Status', 'Actions'].map((h) => (
                    <TableCell
                      key={h}
                      sx={{
                        fontWeight: 700,
                        fontSize: '0.75rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.06em',
                        color: 'text.secondary',
                        py: 1.5,
                      }}
                    >
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {loading ? (
                  <SkeletonRows count={rowsPerPage} />
                ) : paginated.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                      <Icon icon="bx:user-x" fontSize={40} style={{ color: '#bdbdbd', display: 'block', margin: '0 auto 8px' }} />
                      <Typography color="text.secondary">
                        {search || roleFilter !== 'all' ? 'No users match your filters' : 'No users found'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginated.map((u) => {
                    const role = ROLE_CHIP[u.role] ?? ROLE_CHIP.user;
                    const status = STATUS_CHIP[u.status] ?? STATUS_CHIP.active;
                    const initials = getInitials(u.fullname);
                    const avatarColor = stringToColor(u.fullname);
                    return (
                      <TableRow
                        key={u._id}
                        hover
                        sx={{ '&:last-child td': { border: 0 } }}
                      >
                        {/* User cell */}
                        <TableCell sx={{ py: 1.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Avatar
                              src={u.avatar}
                              sx={{ width: 36, height: 36, fontSize: '0.8rem', bgcolor: avatarColor, flexShrink: 0 }}
                            >
                              {initials}
                            </Avatar>
                            <Box>
                              <Typography variant="body2" fontWeight={600} lineHeight={1.3}>
                                {u.fullname}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                @{u.username}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>

                        <TableCell sx={{ py: 1.5 }}>
                          <Typography variant="body2" color="text.secondary">{u.email}</Typography>
                        </TableCell>

                        <TableCell sx={{ py: 1.5 }}>
                          <Typography variant="body2">{u.rank || '—'}</Typography>
                        </TableCell>

                        <TableCell sx={{ py: 1.5 }}>
                          <Typography variant="body2">{u.country || '—'}</Typography>
                        </TableCell>

                        <TableCell sx={{ py: 1.5 }}>
                          <Chip
                            label={role.label}
                            size="small"
                            sx={{
                              backgroundColor: role.bg,
                              color: role.color,
                              fontWeight: 600,
                              fontSize: '0.7rem',
                              height: 22,
                            }}
                          />
                        </TableCell>

                        <TableCell sx={{ py: 1.5 }}>
                          <Chip
                            label={status.label}
                            size="small"
                            sx={{
                              backgroundColor: status.bg,
                              color: status.color,
                              fontWeight: 600,
                              fontSize: '0.7rem',
                              height: 22,
                            }}
                          />
                        </TableCell>

                        <TableCell sx={{ py: 1.5 }}>
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <Tooltip title="Edit user">
                              <IconButton
                                size="small"
                                onClick={() => navigate(`/users/${u._id}/edit`)}
                                sx={{ color: 'text.secondary', '&:hover': { color: BRAND } }}
                              >
                                <Icon icon="bx:edit" fontSize={18} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete user">
                              <IconButton
                                size="small"
                                onClick={() => setDeleteDialog({ open: true, user: u })}
                                sx={{ color: 'text.secondary', '&:hover': { color: BRAND } }}
                              >
                                <Icon icon="bx:trash" fontSize={18} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={filtered.length}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={(_, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
            sx={{ borderTop: `1px solid ${theme.palette.divider}` }}
          />
        </Card>
      </Box>

      {/* ── Delete confirmation dialog ── */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => !deleting && setDeleteDialog({ open: false, user: null })}
        PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Remove User</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to remove{' '}
            <strong>{deleteDialog.user?.fullname}</strong>? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ pb: 2, px: 3 }}>
          <Button
            onClick={() => setDeleteDialog({ open: false, user: null })}
            disabled={deleting}
            sx={{ color: 'text.secondary' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleDeleteConfirm}
            disabled={deleting}
            sx={{ backgroundColor: BRAND, '&:hover': { backgroundColor: BRAND_DARK } }}
          >
            {deleting ? 'Removing…' : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>
    </GuardWrapper>
  );
};

export default AllUsersPage;